import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";

import { EpisodeCard } from "@/components/explore/episode-card";
import { Link } from "@/components/link";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import type { EpisodePage } from "@/lib/queries";

const FEATURED_COUNT = 6;

export function FeaturedEpisodes() {
  const { data, isPending } = useQuery({
    queryFn: async () => {
      const page = await apiFetch<EpisodePage>(
        `/api/public/episodes?limit=${FEATURED_COUNT}&recorded=true`
      );
      return page?.episodes ?? [];
    },
    queryKey: ["episodes", "featured", "recorded"] as const,
    staleTime: 5 * 60 * 1000,
  });

  const episodes = data ?? [];

  // Skeletons match the card layout so the grid doesn't shift when data lands.
  if (isPending) {
    return (
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: FEATURED_COUNT }, (_, i) => (
          <div className="flex animate-pulse flex-col gap-2" key={i}>
            <div className="aspect-square w-full rounded-2xl bg-muted" />
            <div className="h-4 w-3/4 rounded-md bg-muted" />
            <div className="h-3 w-1/2 rounded-md bg-muted" />
          </div>
        ))}
      </section>
    );
  }

  if (episodes.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight">
            Try listening to some of these shows
          </h2>
          <p className="mt-1 text-muted-foreground">
            A taste of what&apos;s in the archive. Press play right here.
          </p>
        </div>
        <Link href="/explore">
          <Button variant="outline">
            Explore the archive <ArrowRight />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {episodes.map((episode) => (
          <EpisodeCard episode={episode} key={episode.id} />
        ))}
      </div>
    </section>
  );
}
